import React, { useEffect, useMemo, useState } from 'react';
import { apiGet, apiPost, apiPut, apiDelete } from '../../api/client';
import MediaPicker from '../../components/admin/MediaPicker';
import AdminModal from '../../components/admin/AdminModal';
import IgdbSearch from '../../components/admin/IgdbSearch';

const emptyReview = {
  id: '',
  title: '',
  poster: '',
  rating: '',
  review: '',
  category: [],
  streamed: false,
  releaseYear: '',
  platinumed: false,
  platform: '',
  allTimeFav: false,
  vodUrl: '',
};

const emptyNotes = { liked: '', disliked: '', verdict: '', extra: '' };

const FILTERS = [
  { id: 'all', label: 'All' },
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'streamed', label: 'Streamed' },
  { id: 'platinum', label: 'Platinum' },
  { id: 'fav', label: 'Favourites' },
];

const SORTS = [
  { id: 'newest', label: 'Newest' },
  { id: 'rating', label: 'Highest rated' },
  { id: 'title', label: 'A–Z' },
];

const isUpcoming = (r) => (r.category || []).includes('Upcoming');

function wordCount(text) {
  return (text.match(/\S+/g) || []).length;
}

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [form, setForm] = useState(emptyReview);
  const [editingId, setEditingId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [sort, setSort] = useState('newest');
  const [newCategory, setNewCategory] = useState('');

  const [aiStatus, setAiStatus] = useState(null);
  const [notes, setNotes] = useState(emptyNotes);
  const [sampleIds, setSampleIds] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [aiError, setAiError] = useState('');
  const [aiUsed, setAiUsed] = useState([]);

  const load = () => apiGet('/reviews').then(setReviews);

  useEffect(() => {
    load().catch(console.error);
    apiGet('/admin/ai/status').then(setAiStatus).catch(() => setAiStatus({ configured: false }));
  }, []);

  const finishedReviews = useMemo(
    () => reviews.filter((r) => !isUpcoming(r) && (r.review || []).join(' ').length > 600).sort((a, b) => b.id - a.id),
    [reviews],
  );

  const defaultSampleIds = useMemo(() => finishedReviews.slice(0, 5).map((r) => r.id), [finishedReviews]);

  const allCategories = useMemo(() => {
    const counts = {};
    reviews.forEach((r) => (r.category || []).forEach((c) => { counts[c] = (counts[c] || 0) + 1; }));
    return Object.keys(counts).sort((a, b) => counts[b] - counts[a]);
  }, [reviews]);

  const stats = useMemo(() => ({
    total: reviews.length,
    upcoming: reviews.filter(isUpcoming).length,
    streamed: reviews.filter((r) => r.streamed).length,
    favorites: reviews.filter((r) => r.allTimeFav).length,
  }), [reviews]);

  const query = search.trim().toLowerCase();
  const visibleReviews = useMemo(() => {
    const list = reviews.filter((r) => {
      if (filter === 'upcoming' && !isUpcoming(r)) return false;
      if (filter === 'streamed' && !r.streamed) return false;
      if (filter === 'platinum' && !r.platinumed) return false;
      if (filter === 'fav' && !r.allTimeFav) return false;
      if (!query) return true;
      return [r.title, r.platform, ...(r.category || [])].join(' ').toLowerCase().includes(query);
    });
    if (sort === 'rating') return list.sort((a, b) => (b.rating ?? -1) - (a.rating ?? -1) || b.id - a.id);
    if (sort === 'title') return list.sort((a, b) => a.title.localeCompare(b.title));
    return list.sort((a, b) => b.id - a.id);
  }, [reviews, filter, query, sort]);

  const openModal = (nextForm, id) => {
    setForm(nextForm);
    setEditingId(id);
    setNotes(emptyNotes);
    setSampleIds(id == null ? defaultSampleIds : finishedReviews.filter((r) => String(r.id) !== String(id)).slice(0, 5).map((r) => r.id));
    setAiError('');
    setAiUsed([]);
    setFormError('');
    setNewCategory('');
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingId(null);
  };

  const startAdd = () => openModal({ ...emptyReview }, null);

  const startEdit = (r) => openModal({
    id: r.id,
    title: r.title,
    poster: r.poster || '',
    rating: r.rating ?? '',
    review: (r.review || []).join('\n\n'),
    category: r.category || [],
    streamed: r.streamed,
    releaseYear: r.releaseYear ?? '',
    platinumed: r.platinumed,
    platform: r.platform || '',
    allTimeFav: r.allTimeFav,
    vodUrl: r.vodUrl || '',
  }, r.id);

  const handleSave = async () => {
    setFormError('');
    setSaving(true);
    const payload = {
      id: form.id ? Number(form.id) : undefined,
      title: form.title,
      poster: form.poster,
      rating: form.rating === '' ? null : Number(form.rating),
      review: form.review.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean),
      category: form.category,
      streamed: form.streamed,
      releaseYear: form.releaseYear ? Number(form.releaseYear) : null,
      platinumed: form.platinumed,
      platform: form.platform,
      allTimeFav: form.allTimeFav,
      vodUrl: form.vodUrl,
    };
    try {
      if (editingId) {
        await apiPut(`/reviews/${editingId}`, payload);
        setMessage(`Saved “${form.title}”.`);
      } else {
        await apiPost('/reviews', payload);
        setMessage(`Published “${form.title}”.`);
      }
      closeModal();
      load();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (r) => {
    if (!window.confirm(`Delete the review for “${r.title}”?`)) return;
    await apiDelete(`/reviews/${r.id}`);
    setMessage(`Deleted “${r.title}”.`);
    load();
  };

  // Poster always; title, year and platform only when still empty.
  const applyIgdb = (game) => {
    setForm((f) => ({
      ...f,
      poster: game.cover || f.poster,
      title: f.title.trim() ? f.title : game.name,
      releaseYear: f.releaseYear !== '' ? f.releaseYear : (game.year ?? ''),
      platform: f.platform.trim() ? f.platform : (game.platforms[0] || ''),
    }));
  };

  const toggleCategory = (cat) => {
    setForm((f) => ({
      ...f,
      category: f.category.includes(cat) ? f.category.filter((c) => c !== cat) : [...f.category, cat],
    }));
  };

  const addCustomCategory = () => {
    const cat = newCategory.trim();
    if (cat && !form.category.includes(cat)) setForm({ ...form, category: [...form.category, cat] });
    setNewCategory('');
  };

  const toggleSample = (id) => {
    setSampleIds((ids) => (ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id].slice(-5)));
  };

  const generate = async () => {
    setAiError('');
    setGenerating(true);
    try {
      const result = await apiPost('/admin/ai/review', {
        title: form.title,
        platform: form.platform,
        releaseYear: form.releaseYear,
        rating: form.rating,
        platinumed: form.platinumed,
        streamed: form.streamed,
        allTimeFav: form.allTimeFav,
        sampleIds,
        ...notes,
      });
      setForm((f) => ({
        ...f,
        review: result.paragraphs.join('\n\n'),
        category: f.category.length ? f.category : result.categories,
      }));
      setAiUsed(result.samples || []);
    } catch (err) {
      setAiError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const reviewWords = wordCount(form.review);
  const paragraphCount = form.review.split(/\n\s*\n/).filter((p) => p.trim()).length;
  const canGenerate = aiStatus?.configured && form.title.trim() && (notes.liked.trim() || notes.disliked.trim() || notes.verdict.trim());

  return (
    <div className="admin-panel-page">
      <div className="admin-panel-toolbar">
        <div className="admin-panel-toolbar-row">
          <div className="admin-stat-chips">
            <div className="admin-stat-chip">
              <span className="admin-stat-value">{stats.total}</span>
              <span className="admin-stat-label">Total</span>
            </div>
            <div className="admin-stat-chip admin-stat-chip--purple">
              <span className="admin-stat-value">{stats.upcoming}</span>
              <span className="admin-stat-label">Upcoming</span>
            </div>
            <div className="admin-stat-chip admin-stat-chip--green">
              <span className="admin-stat-value">{stats.streamed}</span>
              <span className="admin-stat-label">Streamed</span>
            </div>
            <div className="admin-stat-chip admin-stat-chip--gold">
              <span className="admin-stat-value">{stats.favorites}</span>
              <span className="admin-stat-label">Favourites</span>
            </div>
          </div>
          <div className="admin-panel-toolbar-actions">
            <button type="button" className="btn btn-primary" onClick={startAdd}>
              <i className="fas fa-plus" aria-hidden="true" /> Add review
            </button>
          </div>
        </div>
        <div className="admin-filter-row">
          <div className="admin-search">
            <i className="fas fa-search" aria-hidden="true" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search games, platforms, genres…" />
          </div>
          <div className="admin-panel-tabs">
            {FILTERS.map((f) => (
              <button key={f.id} type="button" className={`admin-panel-tab ${filter === f.id ? 'active' : ''}`} onClick={() => setFilter(f.id)}>
                {f.label}
              </button>
            ))}
          </div>
          <select className="admin-select" value={sort} onChange={(e) => setSort(e.target.value)} aria-label="Sort reviews">
            {SORTS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
          <span className="admin-filter-hint">{visibleReviews.length} shown</span>
        </div>
      </div>

      {message && <p className="admin-message">{message}</p>}

      {visibleReviews.length === 0 ? (
        <div className="admin-list-wrap"><div className="admin-list-empty"><p>No reviews match.</p></div></div>
      ) : (
        <div className="admin-review-grid">
          {visibleReviews.map((r) => (
            <article key={r.id} className="admin-review-card">
              <button type="button" className="admin-review-card__poster" onClick={() => startEdit(r)} title="Edit review">
                {r.poster ? <img src={r.poster} alt="" loading="lazy" /> : <span className="admin-review-card__empty"><i className="fas fa-gamepad" aria-hidden="true" /></span>}
                {r.rating != null && <span className="admin-review-card__rating">{r.rating}</span>}
                {isUpcoming(r) && <span className="admin-review-card__flag">Upcoming</span>}
              </button>
              <div className="admin-review-card__body">
                <strong title={r.title}>{r.title}</strong>
                <span className="admin-review-card__meta">
                  {[r.platform, r.releaseYear].filter(Boolean).join(' · ') || `#${r.id}`}
                </span>
                <div className="admin-review-card__icons">
                  {r.streamed && <i className="fab fa-twitch" title="Streamed" aria-label="Streamed" />}
                  {r.platinumed && <i className="fas fa-trophy" title="Platinum" aria-label="Platinum" />}
                  {r.allTimeFav && <i className="fas fa-heart" title="All-time favourite" aria-label="Favourite" />}
                  <span className="admin-review-card__actions">
                    <button type="button" className="admin-icon-btn" onClick={() => startEdit(r)} title="Edit"><i className="fas fa-pen" aria-hidden="true" /></button>
                    <button type="button" className="admin-icon-btn danger" onClick={() => handleDelete(r)} title="Delete"><i className="fas fa-trash" aria-hidden="true" /></button>
                  </span>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}

      <AdminModal
        open={modalOpen}
        as="form"
        onSubmit={handleSave}
        onClose={closeModal}
        size="xl"
        title={editingId ? `Edit review · ${form.title}` : 'New review'}
        footer={(
          <>
            {formError && <span className="admin-modal__error">{formError}</span>}
            <span className="admin-modal__meta">{reviewWords} words · {paragraphCount} paragraphs</span>
            <button type="button" className="btn btn-secondary" onClick={closeModal}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving…' : editingId ? 'Save review' : 'Publish review'}
            </button>
          </>
        )}
      >
        <div className="admin-review-editor">
          <aside className="admin-review-editor__side">
            <div className="admin-review-poster">
              {form.poster ? <img src={form.poster} alt="" /> : <div className="admin-review-poster-empty"><i className="fas fa-image" aria-hidden="true" /></div>}
              {form.rating !== '' && <span className="admin-review-rating-badge">{form.rating}/10</span>}
            </div>
            <IgdbSearch initialQuery={form.title} onSelect={applyIgdb} label="Search IGDB" />
            <MediaPicker value={form.poster} onChange={(url) => setForm({ ...form, poster: url })} placeholder="Poster URL (IGDB etc.)" />
            <div className="admin-review-flag-row">
              <button type="button" className={`admin-flag-toggle ${form.streamed ? 'is-on' : ''}`} onClick={() => setForm({ ...form, streamed: !form.streamed })}>
                <i className="fab fa-twitch" aria-hidden="true" /> Streamed
              </button>
              <button type="button" className={`admin-flag-toggle ${form.platinumed ? 'is-on' : ''}`} onClick={() => setForm({ ...form, platinumed: !form.platinumed })}>
                <i className="fas fa-trophy" aria-hidden="true" /> Platinum
              </button>
              <button type="button" className={`admin-flag-toggle is-fav ${form.allTimeFav ? 'is-on' : ''}`} onClick={() => setForm({ ...form, allTimeFav: !form.allTimeFav })}>
                <i className="fas fa-heart" aria-hidden="true" /> Favourite
              </button>
            </div>
          </aside>

          <div className="admin-review-editor__main">
            <div className="admin-field-row admin-field-row--review">
              <label className="admin-field admin-field--grow">
                <span>Game title</span>
                <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required autoFocus={!editingId} />
              </label>
              <label className="admin-field admin-field--narrow">
                <span>Rating</span>
                <input type="number" min="0" max="10" step="0.5" value={form.rating} onChange={(e) => setForm({ ...form, rating: e.target.value })} placeholder="0–10" />
              </label>
              <label className="admin-field admin-field--narrow">
                <span>Year</span>
                <input type="number" value={form.releaseYear} onChange={(e) => setForm({ ...form, releaseYear: e.target.value })} />
              </label>
              <label className="admin-field">
                <span>Platform</span>
                <input list="review-platforms" value={form.platform} onChange={(e) => setForm({ ...form, platform: e.target.value })} placeholder="PS5 Pro" />
              </label>
              {!editingId && (
                <label className="admin-field admin-field--narrow">
                  <span>ID</span>
                  <input value={form.id} onChange={(e) => setForm({ ...form, id: e.target.value })} placeholder="Auto" />
                </label>
              )}
            </div>
            {form.rating !== '' && (
              <input
                className="admin-range"
                type="range"
                min="0"
                max="10"
                step="0.5"
                value={form.rating}
                onChange={(e) => setForm({ ...form, rating: e.target.value })}
                aria-label="Rating slider"
              />
            )}

            <div className="admin-field">
              <span>Categories</span>
              <div className="admin-chip-picker">
                {[...new Set([...allCategories, ...form.category])].map((cat) => (
                  <button key={cat} type="button" className={`admin-chip ${form.category.includes(cat) ? 'is-on' : ''}`} onClick={() => toggleCategory(cat)}>
                    {cat}
                  </button>
                ))}
                <input
                  className="admin-chip-input"
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustomCategory(); } }}
                  onBlur={addCustomCategory}
                  placeholder="+ new"
                />
              </div>
            </div>

            <label className="admin-field">
              <span>VOD URL</span>
              <input value={form.vodUrl} onChange={(e) => setForm({ ...form, vodUrl: e.target.value })} placeholder="https://youtube.com/…" />
            </label>

            <label className="admin-field admin-field--grow-y">
              <span>Review <em>· blank line between paragraphs</em></span>
              <textarea
                className="admin-review-textarea"
                value={form.review}
                onChange={(e) => setForm({ ...form, review: e.target.value })}
                placeholder="Write it yourself, or fill in the AI writer on the right and press Generate."
              />
            </label>
          </div>

          <aside className="admin-ai-panel">
            <div className="admin-ai-panel__head">
              <i className="fas fa-magic" aria-hidden="true" />
              <div>
                <strong>AI writer</strong>
                <span>Writes the review in your style from your notes.</span>
              </div>
            </div>

            {aiStatus && !aiStatus.configured && (
              <p className="admin-ai-panel__setup">
                Add an AI key to <code>api/.env</code> to turn this on, for example <code>OPENAI_API_KEY=sk-…</code>,
                {' '}<code>ANTHROPIC_API_KEY=…</code> or <code>GEMINI_API_KEY=…</code>. See Settings → AI.
              </p>
            )}

            <label className="admin-field">
              <span><i className="fas fa-thumbs-up" aria-hidden="true" /> What you liked</span>
              <textarea rows={4} value={notes.liked} onChange={(e) => setNotes({ ...notes, liked: e.target.value })} placeholder="Story, characters, combat, a moment that stuck with you…" />
            </label>
            <label className="admin-field">
              <span><i className="fas fa-thumbs-down" aria-hidden="true" /> What you didn&apos;t like</span>
              <textarea rows={3} value={notes.disliked} onChange={(e) => setNotes({ ...notes, disliked: e.target.value })} placeholder="Bugs, pacing, a boss you hated…" />
            </label>
            <label className="admin-field">
              <span><i className="fas fa-gavel" aria-hidden="true" /> Overall verdict</span>
              <textarea rows={2} value={notes.verdict} onChange={(e) => setNotes({ ...notes, verdict: e.target.value })} placeholder="Would you recommend it? Where does it rank?" />
            </label>
            <label className="admin-field">
              <span>Extra notes (optional)</span>
              <textarea rows={2} value={notes.extra} onChange={(e) => setNotes({ ...notes, extra: e.target.value })} placeholder="Platinum difficulty, hours played, DLC…" />
            </label>

            <div className="admin-field">
              <span>Learns your style from ({sampleIds.length}/5)</span>
              <div className="admin-chip-picker admin-chip-picker--samples">
                {finishedReviews.filter((r) => String(r.id) !== String(editingId)).slice(0, 12).map((r) => (
                  <button key={r.id} type="button" className={`admin-chip ${sampleIds.includes(r.id) ? 'is-on' : ''}`} onClick={() => toggleSample(r.id)} title={r.title}>
                    {r.title}
                  </button>
                ))}
              </div>
            </div>

            {aiError && <p className="admin-message admin-message--error">{aiError}</p>}
            {aiUsed.length > 0 && !aiError && (
              <p className="admin-form-hint">Draft written from: {aiUsed.map((s) => s.title).join(', ')}. Read it through and tweak before saving.</p>
            )}

            <button type="button" className="btn btn-primary admin-ai-panel__go" onClick={generate} disabled={!canGenerate || generating}>
              {generating ? <><i className="fas fa-spinner fa-spin" aria-hidden="true" /> Writing…</> : <><i className="fas fa-magic" aria-hidden="true" /> {form.review.trim() ? 'Regenerate review' : 'Generate review'}</>}
            </button>
            {!form.title.trim() && <span className="admin-form-hint">Add the game title first.</span>}
          </aside>
        </div>
        <datalist id="review-platforms">
          {[...new Set(reviews.map((r) => r.platform).filter(Boolean))].map((p) => <option key={p} value={p} />)}
        </datalist>
      </AdminModal>
    </div>
  );
}
